import './home-page.css';
import { useSearchParams } from 'react-router-dom';
import { SalesChannel } from '../../dto/model';
import { validateCalendarParams, QualifyingAnswer } from '../../utils/lead-queue-utils';
import { FlowQuestion } from '../../dto/qualifying-flow-models';
import { SalesCallCalendar } from '../appointment-booking/sales-call-calendar';
import { useState, useEffect } from 'react';
import { Result as AntdResult } from 'antd';
import { Header } from '../header/header';
import { CalendarBackButton, StartBookingButton } from '../appointment-booking/calendar-button';
import { QualifyingQuestions } from '../appointment-booking/qualifying-questions';
import { useMsal } from '@azure/msal-react';
import { GetAccessToken } from '../../utils/auth-utils';
import { scopes } from '../../authConfig';
import { salesAppointmentsService } from '../../services/sales-appointments-service';
import { useQualifyingAnswersState } from '../../store/qualifying-answers-state';
import { useShallow } from 'zustand/shallow';

type HomePageView = 'start' | 'questions' | 'calendar';

export const HomePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { instance, inProgress } = useMsal();
  const CALENDAR_TYPE = {
    SALESCALL2: 'salescall2',
    FIELDSALES: 'fieldsales',
    RETENTIONCALL: 'retentioncall',
    STANDALONE: 'standalone',
  } as const;
  
  const getCalendarType = (type: string | null): string => {
    if (type === CALENDAR_TYPE.SALESCALL2) return CALENDAR_TYPE.SALESCALL2;
    if (type === CALENDAR_TYPE.FIELDSALES) return CALENDAR_TYPE.FIELDSALES;
    if (type === CALENDAR_TYPE.RETENTIONCALL) return CALENDAR_TYPE.RETENTIONCALL;
    return CALENDAR_TYPE.STANDALONE;
  }
  // URL parameters
  const opportunityId = searchParams.get('opportunityId') ?? '';
  const signupId = searchParams.get('signupId') ?? '';
  const type = searchParams.get('type');
  const calendarType = getCalendarType(type);

  // Only the lead queue flow (signupId + type=leadqueue) requires qualifying questions.
  // All other valid entries (opportunityId, or signupId without type) go straight to the calendar.
  const skipQualifyingQuestions = type?.toLowerCase() !== 'leadqueue';
  const [validation] = useState(validateCalendarParams(opportunityId, signupId, type));

  const [view, setView] = useState<HomePageView>(skipQualifyingQuestions ? 'calendar' : 'start');
  // signupId without type=leadqueue is a lead handover — book directly for DigitalSales
  const [salesChannel, setSalesChannel] = useState<SalesChannel | undefined>(signupId && skipQualifyingQuestions ? SalesChannel.DigitalSales : undefined);
  const [questions, setQuestions] = useState<FlowQuestion[]>([]);

  const [storeAnswers, clearAnswers] = useQualifyingAnswersState(useShallow((state) => [state.answers, state.clearAnswers]));

  useEffect(() => {
    if (skipQualifyingQuestions) return;

    const fetchQuestions = async () => {
      try {
        const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
        const result = await salesAppointmentsService.getActiveQualificationFlow(token.accessToken, 'LeadQueue');
        if (result.isSuccess && result.data) {
          setQuestions(result.data);
        }
      } catch (error) {
        console.error('Failed to fetch qualifying questions:', error);
      }
    };
    fetchQuestions();
  }, []);

  const getQualifyingAnswersForBooking = (): QualifyingAnswer[] => {
    return questions
      .filter((fq) => storeAnswers[fq.question.id])
      .map((fq) => ({
        questionId: fq.question.id,
        questionText: JSON.stringify(fq.question.questionText),
        answerText: storeAnswers[fq.question.id],
      }));
  };

  const onBooking = async (isSuccess: boolean) => {
    if (isSuccess) {
      const answers = getQualifyingAnswersForBooking();
      if (answers.length > 0) {
        try {
          const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
          await salesAppointmentsService.saveQualifyingAnswers(token.accessToken, opportunityId, signupId, answers);
        } catch {}
      }
      clearAnswers();
    }
  };

  const handleLoadCalendar = (channel: SalesChannel) => {
    setSalesChannel(channel);
    setView('calendar');
  };

  if (!validation.isValid) {
    return (
      <div className="empty-container">
        <AntdResult status="warning" title={validation.error} />
      </div>
    );
  }

  return (
    <>
      <Header />
      {view === 'start' && (
        <div className="home-page-container">
          <StartBookingButton buttonStyle={undefined} onClick={() => setView('questions')} />
        </div>
      )}
      {view === 'questions' && (
        <div className="home-page-container">
          <QualifyingQuestions questions={questions} loadCalendar={handleLoadCalendar} />
        </div>
      )}
      {/* Back button only shows when the user navigated from qualifying questions to calendar */}
      {view === 'calendar' && !skipQualifyingQuestions && <CalendarBackButton style={{ marginLeft: '75px' }} onClick={() => setView('questions')} />}
      {view === 'calendar' && (
        <div className="calendar-container">
          <SalesCallCalendar opportunityId={opportunityId} signupId={signupId} displayUserInfo onBooking={onBooking} sourceSystemHeader={calendarType} salesChannel={salesChannel} />
        </div>
      )}
    </>
  );
};
